import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import { useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import {
  ApproveRejectActions,
  BaseTextField,
  ControlledTextField,
  SalaryBreakdownTable,
  SectionTitle,
} from 'modules/shared/components';
import {
  calculateSalaryBreakdown,
  formatAmount,
  formatDate,
  getSalaryBreakdownRows,
  limitToTwoDecimals,
} from 'modules/shared/utils';
import { useOfferTiming } from '../../shared/hooks';
import { getLatestOffer } from '../../shared/utils';
import { InlineRejectSection } from '../components';
import { INTERVIEW_ACTION } from '../interview.constants';
import { normalizeOffers } from '../utils';

const CURRENT_ROUND_KEY = 'round3';
const OFFER_STATUS = Object.freeze({
  SENT: 'sent',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
});

/**
 * InterviewRound3
 *
 * Controls salary negotiation, offer generation, and offer status tracking
 * for the final HR interview round.
 *
 * Responsibilities:
 * - Finalize and validate offered salary
 * - Generate and send offer letters
 * - Display offer status history
 * - Gate rejection based on offer state
 *
 * Props:
 * @param {Array} data - Existing offers associated with the candidate.
 * @param {Object} interviewData - Interview-level constraints (e.g. salary cap).
 * @param {Object} candidateData - Candidate information (e.g. expected salary).
 * @param {boolean} readOnly - Disables all actions when true.
 * @param {Function} onApprove - Invoked when an offer is generated.
 * @param {Function} onReject - Invoked when candidate is rejected.
 *
 * Notes:
 * - Uses react-hook-form internally for salary and rejection state.
 * - Offer lifecycle is derived from normalized offer data.
 */
export default function InterviewRound3({
  data = [],
  interviewData,
  candidateData,
  readOnly,
  onApprove,
  onReject,
}) {
  const offers = normalizeOffers(data);
  const latestOffer = getLatestOffer(offers);

  const offerStatus = latestOffer?.offerLetter?.status ?? null;
  const offerTiming = useOfferTiming(latestOffer?.offerLetter?.sendDate);

  const offerState = useMemo(
    () => ({
      hasOffer: offers.length > 0,
      status: offerStatus,
      isAccepted: offerStatus === OFFER_STATUS.ACCEPTED,
      isRejected: offerStatus === OFFER_STATUS.REJECTED,
      isSent: offerStatus === OFFER_STATUS.SENT,
      isExpired: offerStatus === OFFER_STATUS.SENT && !!offerTiming && offerTiming.isExpired,
      isPending: offerStatus === OFFER_STATUS.SENT && !!offerTiming && !offerTiming.isExpired,
    }),
    [offerStatus, offerTiming, offers],
  );

  const methods = useForm({
    defaultValues: {
      round3: { salaryFinalized: Number(latestOffer?.salaryFinalized ?? 0) },
      rejectionReason: '',
    },
  });
  const { watch, trigger, resetField, formState } = methods;

  const permissions = useMemo(
    () => ({
      canEditSalary: !offerState.hasOffer || offerState.isRejected || offerState.isExpired,

      canReject: !offerState.isAccepted,

      canGenerateOffer:
        !readOnly &&
        formState.isValid &&
        (!offerState.hasOffer || offerState.isRejected || offerState.isExpired) &&
        !offerState.isPending,
    }),
    [formState.isValid, offerState, readOnly],
  );

  const [actionMode, setActionMode] = useState<string>(INTERVIEW_ACTION.IDLE);

  const rejectionReason = watch('rejectionReason');
  const salaryFinalized = watch('round3.salaryFinalized');

  const validateOfferForm = () => trigger('round3', { shouldFocus: true });

  const handleGenerateOffer = async () => {
    if (!(await validateOfferForm())) return;

    onApprove?.({
      action: 'approve',
      round3: [
        {
          salaryFinalized,
          offerLetter: {
            status: OFFER_STATUS.SENT,
            id: null,
            name: null,
            url: null,
            sendDate: new Date().toISOString(),
            acceptedDate: null,
            rejectedDate: null,
          },
        },
      ],
      currentRoundKey: CURRENT_ROUND_KEY,
    });
  };

  const resetActionState = () => {
    setActionMode(INTERVIEW_ACTION.IDLE);
    resetField('rejectionReason');
  };

  const handleReject = async () => {
    const isFormValid = await trigger(['rejectionReason', 'round3'], { shouldFocus: true });
    if (!isFormValid) return;
    onReject?.({ round3: { salaryFinalized }, rejectionReason, action: INTERVIEW_ACTION.REJECT });
  };

  return (
    <FormProvider {...methods}>
      <form className='column gap-4'>
        <SalaryNegotiationSection
          editable={permissions.canEditSalary && !readOnly}
          salaryCap={interviewData?.salaryCap}
          candidateExpectation={candidateData?.expectedSalary}
        />

        <OfferGenerationSection
          disabled={!permissions.canGenerateOffer}
          onGenerate={handleGenerateOffer}
          isRegenerate={offerState.hasOffer}
        />
        <OfferLetterSection
          offers={offers}
          latestOffer={latestOffer}
          latestOfferTiming={offerTiming}
        />

        {permissions.canReject && actionMode === INTERVIEW_ACTION.IDLE && (
          <ApproveRejectActions
            showApprove={false}
            onReject={() => setActionMode(INTERVIEW_ACTION.REJECT)}
          />
        )}

        {permissions.canReject && actionMode === INTERVIEW_ACTION.REJECT && (
          <InlineRejectSection
            name='rejectionReason'
            onCancel={resetActionState}
            onConfirm={handleReject}
          />
        )}
      </form>
    </FormProvider>
  );
}

function SalaryNegotiationSection({ editable, salaryCap, candidateExpectation }) {
  const { watch } = useFormContext();
  const finalizedSalary = watch('round3.salaryFinalized');

  const breakdown = calculateSalaryBreakdown(finalizedSalary);
  const salaryTableRows = getSalaryBreakdownRows(breakdown);

  return (
    <div className='column gap-3'>
      <SectionTitle
        title='ROUND 3 - HR'
        action={<span className='font-medium'>Candidate Test Score</span>}
      />
      <div className='column'>
        <div className='grid grid--3-cols'>
          <label htmlFor='salaryExpectation'>Salary Expectation</label>
          <div></div>
          <BaseTextField id='salaryExpectation' disabled value={candidateExpectation} prefix='₹' onChange={() => {}} />
        </div>
        <div className='grid grid--3-cols'>
          <label htmlFor='salaryFinalized'>Salary Finalized</label>
          <div>CAP: {formatAmount(salaryCap ?? 0)}</div>
          <ControlledTextField
            id='salaryFinalized'
            name='round3.salaryFinalized'
            type='number'
            disabled={!editable}
            prefix='₹'
            rules={{
              required: 'Finalized salary is required',
              min: { value: 1, message: 'Min salary is ₹1' },
            }}
            showErrorInline
            hideArrow
            onBlurNormalize={(v: string) => String(limitToTwoDecimals(v))}
            sx={{
              '& .MuiOutlinedInput-root.Mui-disabled': {
                pointerEvents: 'none',
                cursor: 'default',
              },
            }}
          />
        </div>
        <SalaryBreakdownTable rows={salaryTableRows} />
      </div>
    </div>
  );
}

function OfferGenerationSection({ disabled, onGenerate, isRegenerate }) {
  return (
    <div className='column '>
      <button
        type='button'
        className='btn cta-outlined-btn self-end'
        disabled={disabled}
        onClick={onGenerate}
      >
        {isRegenerate ? 'Regenerate & Send Offer Letter' : 'Generate & Send Offer Letter'}
      </button>
    </div>
  );
}

function OfferLetterSection({ offers = [], latestOffer, latestOfferTiming }) {
  return (
    <div className='column gap-3'>
      <SectionTitle title='OFFER LETTER' />
      {offers.length === 0 ? (
        <div className='grid grid--3-cols'>
          <span>Offer letter sent to candidate</span>
          <div className='column gap-1 text--secondary'>
            <div className='row'>
              <Icon icon='mdi:account-pending-outline' width={18} />
              <span>Offer letter not yet sent</span>
            </div>
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
              <div className='row'>
                <Icon icon='mdi:account-pending-outline' width={18} />
                <span>Approval pending from candidate</span>
              </div>
              <span>Time left: 48 Hrs</span>
            </Box>
          </div>
        </div>
      ) : (
        offers.map((offer, idx) => (
          <OfferLetterItem
            key={idx}
            offer={offer}
            timing={offer === latestOffer ? latestOfferTiming : null}
          />
        ))
      )}
    </div>
  );
}

function OfferLetterItem({ offer = {} as Record<string, any>, timing }: { offer?: Record<string, any>; timing?: any }) {
  const { offerLetter } = offer;
  const offerStatus = offerLetter?.status;
  const isLatestOffer = !!timing;

  return (
    <div className='grid grid--3-cols grid-start'>
      <span>Offer letter sent to candidate</span>

      <div className='column gap-1 text--secondary'>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
          <div className='row'>
            <Icon icon='tabler:check' width={18} />
            <span>Sent to candidate</span>
          </div>
          <span>{formatDate(offerLetter?.sendDate)}</span>
        </Box>

        {offerStatus === OFFER_STATUS.ACCEPTED && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
            <div className='row'>
              <Icon icon='tabler:check' width={18} />
              <span>Accepted by candidate</span>
            </div>
            <span>{formatDate(offerLetter?.acceptedDate)}</span>
          </Box>
        )}

        {offerStatus === OFFER_STATUS.REJECTED && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', '& svg': { ml: -0.5 } }}>
            <div className='row'>
              <Icon icon='basil:cross-outline' width={28} />
              <span>Rejected by candidate</span>
            </div>
            <span>{formatDate(offerLetter?.rejectedDate)}</span>
          </Box>
        )}

        {offerStatus === OFFER_STATUS.SENT && isLatestOffer && !timing.isExpired && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
            <div className='row'>
              <Icon icon='mdi:account-pending-outline' width={18} />
              <span>Approval pending from candidate</span>
            </div>
            <span>Time left: {timing.hoursLeft} hrs</span>
          </Box>
        )}

        {offerStatus === OFFER_STATUS.SENT && timing?.isExpired && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
            <div className='row'>
              <Icon icon='zondicons:exclamation-outline' width={18} />
              <span>Candidate failed to take action</span>
            </div>
            <span>Time left: {timing.hoursLeft} hrs</span>
          </Box>
        )}
      </div>

      <MuiLink
        href={offerLetter?.url}
        target='_blank'
        rel='noopener noreferrer'
        sx={{ color: 'grey.500', justifySelf: 'end', padding: 0 }}
      >
        view
      </MuiLink>
    </div>
  );
}
